import {
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react'

/** What the keyboard can land on in a box. */
const TABBABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export type Popover = {
	open: boolean
	/** The trigger's click: opens the box, or closes it with focus back on the trigger. */
	toggle: () => void
	/** The control that opens the box; focus goes back to it. */
	triggerRef: RefObject<HTMLButtonElement>
	/** The open box, which must be focusable (`tabIndex={-1}`); what counts as inside. */
	boxRef: RefObject<HTMLDivElement>
}

const within = (
	container: Element | null,
	target: EventTarget | null
): boolean =>
	container !== null && target instanceof Node && container.contains(target)

/**
 * The open or closed state of a non-modal box anchored to a trigger, and the
 * keyboard and pointer manners that go with it:
 *
 * - opening moves focus to the box's first control (the box itself when it
 *   has none);
 * - Escape, and the trigger again, close it and give focus back to the
 *   trigger;
 * - a press or focus outside the box and its trigger closes it too, but
 *   leaves focus where the trigger's user went - taking it back would fight
 *   the click that dismissed the box.
 *
 * The trigger counts as inside so that pressing it is the trigger's click and
 * nothing else: a dismissal on the press would let the click open it again.
 */
export const usePopover = (): Popover => {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const boxRef = useRef<HTMLDivElement>(null)

	const close = useCallback((returnFocus: boolean): void => {
		setOpen(false)
		if (returnFocus) {
			triggerRef.current?.focus()
		}
	}, [])

	const toggle = useCallback((): void => {
		if (open) {
			close(true)
		} else {
			setOpen(true)
		}
	}, [open, close])

	useLayoutEffect(() => {
		if (!open) {
			return
		}
		const box = boxRef.current
		const first = box?.querySelector<HTMLElement>(TABBABLE) ?? box
		first?.focus()
	}, [open])

	useEffect(() => {
		if (!open) {
			return
		}
		const outside = (target: EventTarget | null): boolean =>
			!within(boxRef.current, target) && !within(triggerRef.current, target)
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				close(true)
			}
		}
		const onOutside = (event: Event): void => {
			if (outside(event.target)) {
				close(false)
			}
		}
		document.addEventListener('keydown', onKeyDown)
		document.addEventListener('pointerdown', onOutside)
		document.addEventListener('focusin', onOutside)
		return () => {
			document.removeEventListener('keydown', onKeyDown)
			document.removeEventListener('pointerdown', onOutside)
			document.removeEventListener('focusin', onOutside)
		}
	}, [open, close])

	return { open, toggle, triggerRef, boxRef }
}
